import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

export type ModoInvitacion = 'APAGADA' | 'PILOTO' | 'TODOS';
export type EmpresaCobro = 'MT' | 'CT';

/**
 * Domiciliación de BDV y Mercantil con las cuentas de CrediTotal (previo a multicomercio).
 * El token lo agrega el interceptor de autenticación, como en los demás servicios.
 */
@Injectable({
  providedIn: 'root'
})
export class DomiciliacionCtService {
  private env: any = environment;
  private baseUrl: string = this.env.BASE_LA;

  constructor(private http: HttpClient) {}

  /** Estado: invitación, empresa que cobra, aceptaciones, datos de cobro e historiales. */
  getEstado(): Promise<any> {
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/domiciliacion-ct`));
  }

  /** Revisiones para pasar a CrediTotal. */
  getVerificacion(): Promise<any> {
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/domiciliacion-ct/verificacion`));
  }

  cambiarInvitacion(modo: ModoInvitacion, motivo: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.baseUrl}/api/domiciliacion-ct/invitacion`, { modo, motivo }));
  }

  cambiarEmpresa(empresa: EmpresaCobro, motivo: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.baseUrl}/api/domiciliacion-ct/empresa`, { empresa, motivo }));
  }

  /** Grupo piloto: personas (cédula y nombre) y últimos cambios. */
  getPiloto(): Promise<any> {
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/domiciliacion-ct/piloto`));
  }

  agregarAlPiloto(cedula: string, motivo: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.baseUrl}/api/domiciliacion-ct/piloto`, { cedula, motivo }));
  }

  quitarDelPiloto(code: string, motivo: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.baseUrl}/api/domiciliacion-ct/piloto/quitar`, { code, motivo }));
  }

  /** Avance de la aceptación de los términos de CrediTotal (con refrescar=1 se salta la caché). */
  getAvance(refrescar = false): Promise<any> {
    const q = refrescar ? '?refrescar=1' : '';
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/domiciliacion-ct/avance${q}`));
  }

  /** Excel con una fila por cuenta del universo (aceptó o no). Puede tardar: son ~150.000 cuentas. */
  descargarAvanceExcel(): Promise<Blob> {
    return lastValueFrom(this.http.get(`${this.baseUrl}/api/domiciliacion-ct/avance/excel`, { responseType: 'blob' }));
  }

  /** Última corrida del proceso diario lanzada desde la consola (corriendo, resultado, error). */
  getProceso(): Promise<any> {
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/domiciliacion-ct/proceso`));
  }

  /** Lanza el proceso diario (crear afiliaciones CT y activar) en segundo plano; con simulacion no escribe nada. */
  correrProceso(simulacion: boolean, motivo?: string): Promise<any> {
    return lastValueFrom(this.http.post<any>(`${this.baseUrl}/api/domiciliacion-ct/proceso`, { simulacion, motivo }));
  }

  /** Estado de las cuentas de un cliente. Nunca lanza: si falla o tarda más de 8 s devuelve []. */
  async getCuentasCliente(code: string | number): Promise<any[]> {
    if (!code) return [];
    try {
      const r = await lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/domiciliacion-ct/cliente/${encodeURIComponent(String(code))}`).pipe(timeout(8000)));
      return Array.isArray(r?.cuentas) ? r.cuentas : [];
    } catch {
      return [];
    }
  }

  /**
   * Busca la cuenta de CrediTotal de una cuenta de la pantalla: primero por _id (payment_method_customer);
   * si no hay id o no coincide, por banco + producto (se queda con el estado más avanzado).
   * Banco: se compara el _id, el código o el nombre, sin mayúsculas ni tildes.
   */
  buscarCuenta(cuentas: any[], o: { id?: string; bank?: any; product?: string }): any | null {
    if (!cuentas?.length) return null;
    if (o.id) {
      const x = cuentas.find(c => c.payment_method_customer && String(c.payment_method_customer) === String(o.id));
      if (x) return x;
    }
    const n = (v: any) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    const claves = [o.bank?._id, o.bank?.code, o.bank?.name, typeof o.bank === 'string' ? o.bank : null].map(n).filter(Boolean);
    if (!claves.length) return null;
    const orden = ['COBRANDO_CT', 'LISTA_PARA_CT', 'ACEPTADA', 'PENDIENTE'];
    const prod = o.product || 'CREDIT';
    const cand = cuentas.filter(c => (c.product_type || 'CREDIT') === prod
      && (claves.includes(n(c.bank)) || claves.includes(n(c.banco))));
    cand.sort((a, b) => orden.indexOf(a.estado) - orden.indexOf(b.estado) || 0);
    return cand.find(c => orden.includes(c.estado)) || cand[0] || null;
  }
}
